import type { Position } from '../types';
import type { PlacedWord } from './PlacedWord';
import type { BoardCell } from './BoardCell';
import { createBoardCell } from './BoardCell';
import type { BonusSquare } from '../utils/BonusSquareGenerator';
import { generateBonusSquares, getBonusAtPosition } from '../utils/BonusSquareGenerator';
import { BOARD_SIZE, CENTER_POSITION } from '../constants/gameConstants';

export interface Board {
  size: 21; // Fixed board dimension
  cells: BoardCell[][]; // 21x21 2D array
  centerPosition: Position; // Always (10, 10)
  placedWords: PlacedWord[]; // History of all placed words
  isEmpty: boolean; // True if no words placed yet
  bonusSquares: BonusSquare[]; // Randomized bonus square positions
  seed: string | number | null; // Random seed for deterministic bonus generation
}

/**
 * Create a new game board
 */
export function createBoard(seed?: string | number): Board {
  // Generate bonus squares first
  const bonusSquares = generateBonusSquares(seed);
  
  // Create 21x21 grid of cells
  const cells: BoardCell[][] = [];
  for (let row = 0; row < BOARD_SIZE; row++) {
    cells[row] = [];
    for (let col = 0; col < BOARD_SIZE; col++) {
      const bonusType = getBonusAtPosition(bonusSquares, { row, col });
      cells[row][col] = createBoardCell(row, col, bonusType);
    }
  }
  
  return {
    size: 21,
    cells,
    centerPosition: CENTER_POSITION,
    placedWords: [],
    isEmpty: true,
    bonusSquares,
    seed: seed ?? null,
  };
}

/**
 * Get cell at a specific position
 */
export function getCell(board: Board, position: Position): BoardCell | null {
  if (position.row < 0 || position.row >= BOARD_SIZE || position.col < 0 || position.col >= BOARD_SIZE) {
    return null;
  }
  return board.cells[position.row][position.col];
}

/**
 * Update a cell on the board
 */
export function updateCell(board: Board, position: Position, cell: BoardCell): Board {
  const newCells = board.cells.map((row, rowIndex) =>
    row.map((c, colIndex) =>
      rowIndex === position.row && colIndex === position.col ? cell : c
    )
  );
  
  return {
    ...board,
    cells: newCells,
  };
}

/**
 * Add a placed word to the board
 */
export function addPlacedWord(board: Board, word: PlacedWord): Board {
  return {
    ...board,
    placedWords: [...board.placedWords, word],
    isEmpty: false,
  };
}

/**
 * Get all adjacent cells to a position
 */
export function getAdjacentCells(board: Board, position: Position): BoardCell[] {
  const adjacent: BoardCell[] = [];
  const directions = [
    { row: -1, col: 0 }, // up
    { row: 1, col: 0 },  // down
    { row: 0, col: -1 }, // left
    { row: 0, col: 1 },  // right
  ];
  
  for (const dir of directions) {
    const cell = getCell(board, {
      row: position.row + dir.row,
      col: position.col + dir.col,
    });
    if (cell) {
      adjacent.push(cell);
    }
  }
  
  return adjacent;
}

/**
 * Check if a position is the center of the board
 */
export function isCenterPosition(board: Board, position: Position): boolean {
  return position.row === board.centerPosition.row && position.col === board.centerPosition.col;
}
